import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuthStore } from '../../store';
import {
  FileText, ArrowUpRight, ArrowDownLeft, Download, X,
  TrendingUp, TrendingDown, Wallet, Search, Loader2, Database, FileSpreadsheet, FileDown,
} from 'lucide-react';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import IyzicoPaymentModal from '../../components/IyzicoPaymentModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (amount) =>
  new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(amount || 0);

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('tr-TR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
  });
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('tr-TR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const STATUS_LABEL = { pending: 'Beklemede', preparing: 'Hazırlanıyor', shipped: 'Kargoda', in_transit: 'Sevkiyatta', completed: 'Tamamlandı', cancelled: 'İptal', paid: 'Ödendi', wolvox: 'Wolvox' };
const PAYMENT_STATUS_LABEL = { paid: 'Ödendi', pending: 'Bekliyor', on_account: 'Cari', failed: 'Başarısız' };
const statusBadge = (s) => {
  if (s === 'completed' || s === 'paid') return 'bg-green-50 text-green-700';
  if (s === 'cancelled' || s === 'failed') return 'bg-[#eef0f7] text-[#141a3d]';
  if (s === 'pending') return 'bg-amber-50 text-amber-700';
  if (s === 'wolvox') return 'bg-indigo-50 text-indigo-700';
  return 'bg-gray-100 text-gray-700';
};

export default function DealerAccount() {
  const { token } = useAuthStore();
  const [data, setData] = useState({ movements: [], summary: { total_debit: 0, total_credit: 0, balance: 0 }, wolvox: { linked: false } });
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all'); // all | local | wolvox
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderLoading, setOrderLoading] = useState(false);
  const [showPayMethod, setShowPayMethod] = useState(false);
  const [showIyzico, setShowIyzico] = useState(false);
  const [showBankInfo, setShowBankInfo] = useState(false);

  const downloadExport = async (format) => {
    try {
      const res = await axios.get(`${API_URL}/api/dealer/account-movements/export?format=${format}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const ext = format === 'pdf' ? 'pdf' : 'xlsx';
      const mime = format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const url = URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `cari_hareketler_${new Date().toISOString().slice(0,10)}.${ext}`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success(format === 'pdf' ? 'PDF indirildi' : 'Excel indirildi');
    } catch {
      toast.error('Dışa aktarma başarısız');
    }
  };

  const openOrderDetail = async (orderId) => {
    if (!orderId) return;
    setOrderLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/orders/${orderId}`, { headers: { Authorization: `Bearer ${token}` } });
      setSelectedOrder(res.data);
    } catch { toast.error('Sipariş detayı alınamadı'); }
    finally { setOrderLoading(false); }
  };

  const downloadOrderPdf = async (order) => {
    try {
      const res = await axios.get(`${API_URL}/api/orders/${order.id}/pdf`, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = `siparis_${order.order_number}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success('PDF indirildi');
    } catch { toast.error('PDF indirilemedi'); }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API_URL}/api/dealer/account-movements`, { headers: { Authorization: `Bearer ${token}` } });
        setData(res.data);
      } catch { toast.error('Cari hareketler yüklenemedi'); }
      finally { setLoading(false); }
    };
    fetchData();
  }, [token]);

  const { movements, summary } = data;

  // Filter
  const filtered = movements.filter(m => {
    if (sourceFilter !== 'all' && m.source !== sourceFilter) return false;
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      const matchText = `${m.description || ''} ${m.order_number || ''} ${m.evrak_no || ''}`.toLowerCase();
      if (!matchText.includes(q)) return false;
    }
    if (dateRange !== 'all' && m.date) {
      const days = dateRange === 'week' ? 7 : dateRange === 'month' ? 30 : 90;
      if (new Date(m.date) < new Date(Date.now() - days * 86400000)) return false;
    }
    return true;
  });

  const { total_debit: totalDebit, total_credit: totalCredit, balance } = summary;

  return (
    <div className="space-y-3 sm:space-y-4 pb-20" data-testid="dealer-account">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg sm:text-xl font-bold text-gray-900">Cari Hareketler</h1>
          <p className="text-xs sm:text-sm text-gray-500">Hesap hareketleriniz</p>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 text-gray-600 rounded-lg text-xs sm:text-sm hover:bg-gray-50" data-testid="export-btn">
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">Dışa Aktar</span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuItem onClick={() => downloadExport('excel')} data-testid="export-excel">
              <FileSpreadsheet className="w-4 h-4 mr-2 text-green-600" />Excel olarak indir
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => downloadExport('pdf')} data-testid="export-pdf">
              <FileDown className="w-4 h-4 mr-2 text-[#1a224f]" />PDF olarak indir
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-2 sm:gap-4">
        <div className="bg-white rounded-xl border border-gray-100 p-3 sm:p-4">
          <div className="flex items-center gap-2 mb-1">
            <div className="p-1.5 rounded-lg bg-gray-100 shrink-0"><TrendingUp className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-500" /></div>
            <span className="text-[10px] sm:text-xs text-gray-500">Toplam Borç</span>
          </div>
          <p className="text-sm sm:text-lg font-bold text-gray-900 truncate" data-testid="total-debit">{formatCurrency(totalDebit)}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-3 sm:p-4">
          <div className="flex items-center gap-2 mb-1">
            <div className="p-1.5 rounded-lg bg-gray-100 shrink-0"><TrendingDown className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-500" /></div>
            <span className="text-[10px] sm:text-xs text-gray-500">Toplam Alacak</span>
          </div>
          <p className="text-sm sm:text-lg font-bold text-gray-900 truncate" data-testid="total-credit">{formatCurrency(totalCredit)}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-3 sm:p-4">
          <div className="flex items-center gap-2 mb-1">
            <div className={`p-1.5 rounded-lg shrink-0 ${balance > 0 ? 'bg-[#eef0f7]' : 'bg-gray-100'}`}>
              <Wallet className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${balance > 0 ? 'text-[#1a224f]' : 'text-gray-500'}`} />
            </div>
            <span className="text-[10px] sm:text-xs text-gray-500">Bakiye</span>
          </div>
          <p className={`text-sm sm:text-lg font-bold truncate tabular-nums ${balance > 0 ? 'text-[#1a224f]' : 'text-gray-900'}`} data-testid="balance">{formatCurrency(balance)}</p>
          <p className="text-[9px] sm:text-[10px] text-gray-400">{balance > 0 ? 'Borçlu' : balance < 0 ? 'Alacaklı' : 'Dengede'}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap sm:flex-nowrap">
        <div className="relative flex-1 min-w-[150px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text" placeholder="Ara..." value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#3a4680]/20"
            data-testid="search-input"
          />
        </div>
        <Select value={sourceFilter} onValueChange={setSourceFilter}>
          <SelectTrigger className="w-auto min-w-[110px] h-9 px-2 sm:px-3 border-gray-200 rounded-lg text-xs sm:text-sm bg-white" data-testid="source-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tüm Kaynaklar</SelectItem>
            <SelectItem value="local">B2B</SelectItem>
            <SelectItem value="wolvox">Wolvox</SelectItem>
          </SelectContent>
        </Select>
        <Select value={dateRange} onValueChange={setDateRange}>
          <SelectTrigger className="w-auto min-w-[100px] h-9 px-2 sm:px-3 border-gray-200 rounded-lg text-xs sm:text-sm bg-white" data-testid="date-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tümü</SelectItem>
            <SelectItem value="week">7 Gün</SelectItem>
            <SelectItem value="month">30 Gün</SelectItem>
            <SelectItem value="quarter">90 Gün</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Movements Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="bg-gray-50 border-b border-gray-200 px-4 py-2.5 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-900">Hesap Hareketleri</h2>
          <span className="text-[11px] text-gray-500">{filtered.length} kayıt</span>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-10">
            <FileText className="w-10 h-10 mx-auto text-gray-300 mb-2" />
            <p className="text-sm text-gray-500">Hareket bulunamadı</p>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="bg-gray-50/50 border-b border-gray-100">
                    <th className="w-10 px-2 py-2 text-center text-xs font-semibold text-gray-500">#</th>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500">Tarih</th>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500">Açıklama</th>
                    <th className="text-center px-3 py-2 text-xs font-semibold text-gray-500 w-24">Kaynak</th>
                    <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500 w-32">Borç</th>
                    <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500 w-32">Alacak</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((m, i) => {
                    const clickable = !!m.order_id;
                    return (
                      <tr key={m.id} className={`border-b border-gray-100 last:border-0 ${i % 2 === 1 ? 'bg-gray-50/30' : 'bg-white'} ${clickable ? 'hover:bg-gray-100/50 cursor-pointer' : ''}`} onClick={() => clickable && openOrderDetail(m.order_id)} data-testid={`movement-row-${i}`}>
                        <td className="px-2 py-2.5 text-center text-gray-400 text-xs">{i + 1}</td>
                        <td className="px-3 py-2.5 text-gray-500 text-xs whitespace-nowrap">{formatDateTime(m.date)}</td>
                        <td className="px-3 py-2.5">
                          <div className="flex items-center gap-2">
                            {m.type === 'credit' ? <ArrowDownLeft className="w-3.5 h-3.5 text-green-500 shrink-0" /> : <ArrowUpRight className="w-3.5 h-3.5 text-[#3a4680] shrink-0" />}
                            <div className="min-w-0">
                              <p className="font-medium text-gray-900 truncate">{m.description}</p>
                              {(m.evrak_no || m.order_number) && (
                                <p className="text-[10px] text-gray-400 font-mono">{m.evrak_no || m.order_number}</p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-2.5 text-center">
                          {m.source === 'wolvox' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-indigo-50 text-indigo-700">
                              <Database className="w-2.5 h-2.5" />Wolvox
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-700">B2B</span>
                          )}
                        </td>
                        <td className="px-3 py-2.5 text-right font-medium tabular-nums text-[#1a224f]">{m.type === 'debit' ? formatCurrency(m.amount) : ''}</td>
                        <td className="px-3 py-2.5 text-right font-medium tabular-nums text-green-600">{m.type === 'credit' ? formatCurrency(m.amount) : ''}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-gray-50 border-t-2 border-gray-200">
                  <tr>
                    <td colSpan="4" className="px-3 py-2.5 font-semibold text-gray-900">Toplam</td>
                    <td className="px-3 py-2.5 text-right font-bold tabular-nums text-[#1a224f]">{formatCurrency(totalDebit)}</td>
                    <td className="px-3 py-2.5 text-right font-bold tabular-nums text-green-600">{formatCurrency(totalCredit)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden divide-y divide-gray-100">
              {filtered.map(m => (
                <div key={m.id} className={`flex items-center gap-3 px-3 py-3 ${m.order_id ? 'hover:bg-gray-50 cursor-pointer' : ''}`} onClick={() => openOrderDetail(m.order_id)}>
                  {m.type === 'credit' ? <ArrowDownLeft className="w-4 h-4 text-green-500 shrink-0" /> : <ArrowUpRight className="w-4 h-4 text-[#3a4680] shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{m.description}</p>
                    <div className="flex items-center gap-1.5 mt-0.5 text-[10px] text-gray-400">
                      <span>{formatDate(m.date)}</span>
                      <span className="text-gray-300">·</span>
                      {m.source === 'wolvox' ? (
                        <span className="inline-flex items-center gap-0.5 text-indigo-600 font-medium">
                          <Database className="w-2.5 h-2.5" />Wolvox
                        </span>
                      ) : (
                        <span className="text-gray-500">B2B</span>
                      )}
                    </div>
                  </div>
                  <p className={`text-sm font-bold tabular-nums shrink-0 ${m.type === 'credit' ? 'text-green-600' : 'text-[#1a224f]'}`}>
                    {m.type === 'credit' ? '+' : '-'}{formatCurrency(m.amount)}
                  </p>
                </div>
              ))}
              <div className="px-3 py-3 bg-gray-50 space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-500">Toplam Borç</span>
                  <span className="font-bold text-[#1a224f] tabular-nums">{formatCurrency(totalDebit)}</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-500">Toplam Alacak</span>
                  <span className="font-bold text-green-600 tabular-nums">{formatCurrency(totalCredit)}</span>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Balance Footer */}
      <div className={`rounded-lg p-4 sm:p-5 border ${balance > 0 ? 'bg-[#eef0f7] border-[#b3bbdc]' : 'bg-gray-50 border-gray-200'}`}>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs sm:text-sm text-gray-500">Güncel Bakiye</p>
            <p className={`text-xl sm:text-2xl font-bold mt-0.5 tabular-nums ${balance > 0 ? 'text-[#1a224f]' : 'text-gray-900'}`}>{formatCurrency(Math.abs(balance))}</p>
            <p className="text-[10px] sm:text-xs text-gray-400 mt-0.5">{balance > 0 ? 'Borçlusunuz' : balance < 0 ? 'Alacaklısınız' : 'Hesabınız dengede'}</p>
          </div>
          {balance > 0 && (
            <button onClick={() => setShowPayMethod(true)} className="px-4 py-2 sm:px-5 sm:py-2.5 bg-[#1a224f] text-white rounded-lg text-sm font-semibold hover:bg-[#141a3d] shrink-0" data-testid="pay-cta">
              Ödeme Yap
            </button>
          )}
        </div>
      </div>

      {/* Payment Method Picker */}
      {showPayMethod && (
        <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/60" onClick={() => setShowPayMethod(false)}>
          <div className="bg-white w-full sm:max-w-sm sm:rounded-2xl rounded-t-2xl shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h2 className="text-base font-bold text-gray-900">Ödeme Yöntemi</h2>
                <p className="text-[11px] text-gray-500">Toplam Borç: {formatCurrency(balance)}</p>
              </div>
              <button onClick={() => setShowPayMethod(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-4 h-4 text-gray-500" /></button>
            </div>
            <div className="p-4 space-y-2">
              <button onClick={() => { setShowPayMethod(false); setShowIyzico(true); }} className="w-full p-4 border border-gray-200 rounded-lg flex items-center gap-3 hover:bg-[#eef0f7] hover:border-[#8b96c4] transition-colors" data-testid="pay-with-card">
                <div className="p-2 bg-[#eef0f7] rounded-lg">
                  <svg className="w-5 h-5 text-[#1a224f]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" /></svg>
                </div>
                <div className="flex-1 text-left">
                  <p className="text-sm font-semibold text-gray-900">Kredi Kartı</p>
                  <p className="text-[11px] text-gray-500">Iyzico ile güvenli ödeme · 3D Secure</p>
                </div>
              </button>
              <button onClick={() => { setShowPayMethod(false); setShowBankInfo(true); }} className="w-full p-4 border border-gray-200 rounded-lg flex items-center gap-3 hover:bg-blue-50 hover:border-blue-300 transition-colors" data-testid="pay-with-bank">
                <div className="p-2 bg-blue-50 rounded-lg">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
                </div>
                <div className="flex-1 text-left">
                  <p className="text-sm font-semibold text-gray-900">Havale / EFT</p>
                  <p className="text-[11px] text-gray-500">Banka hesabımıza havale yapın</p>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bank Info Modal (havale bilgileri) */}
      {showBankInfo && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/60" onClick={() => setShowBankInfo(false)}>
          <div className="bg-white rounded-2xl max-w-md w-full shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-base font-bold text-gray-900">Havale/EFT Bilgileri</h2>
              <button onClick={() => setShowBankInfo(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-4 h-4 text-gray-500" /></button>
            </div>
            <div className="p-4">
              <p className="text-xs text-gray-500 mb-3">Aşağıdaki hesap bilgilerine havale yapabilirsiniz. Açıklamaya bayi kodunuzu yazınız.</p>
              <a href="/dealer/payments" className="block text-center py-2.5 bg-[#1a224f] text-white rounded-lg text-sm font-semibold hover:bg-[#141a3d]">Banka Bilgilerini Aç</a>
            </div>
          </div>
        </div>
      )}

      {/* Iyzico Modal for Cari Payment */}
      <IyzicoPaymentModal
        open={showIyzico}
        onClose={() => setShowIyzico(false)}
        kind="cari"
        amount={balance}
        title="Cari Borç Ödemesi"
        subtitle={`Mevcut bakiye: ${formatCurrency(balance)}`}
        onSuccess={() => {
          setShowIyzico(false);
          toast.success('Ödeme alındı, bakiyeniz güncelleniyor...');
          setTimeout(() => window.location.reload(), 1500);
        }}
      />

      {/* Order Detail Modal */}
      {(selectedOrder || orderLoading) && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 pb-20 sm:pb-4 bg-black/50" onClick={() => setSelectedOrder(null)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            {orderLoading ? (
              <div className="p-12 text-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400 mx-auto" /></div>
            ) : selectedOrder && (
              <>
                <div className="sticky top-0 bg-white border-b border-gray-100 p-4 flex items-center justify-between">
                  <div>
                    <h2 className="text-base font-bold text-gray-900">#{selectedOrder.order_number}</h2>
                    <p className="text-xs text-gray-500">{formatDateTime(selectedOrder.created_at)}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => downloadOrderPdf(selectedOrder)} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#1a224f] text-white rounded-lg text-xs font-medium hover:bg-[#141a3d]">
                      <Download className="w-3.5 h-3.5" />PDF İndir
                    </button>
                    <button onClick={() => setSelectedOrder(null)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-4 h-4 text-gray-500" /></button>
                  </div>
                </div>
                <div className="p-4 space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                      <p className="text-[10px] text-gray-400 uppercase font-semibold mb-1.5">Sipariş Durumu</p>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusBadge(selectedOrder.status)}`}>
                        {STATUS_LABEL[selectedOrder.status] || selectedOrder.status}
                      </span>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                      <p className="text-[10px] text-gray-400 uppercase font-semibold mb-1.5">Ödeme</p>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusBadge(selectedOrder.payment_status)}`}>
                        {PAYMENT_STATUS_LABEL[selectedOrder.payment_status] || selectedOrder.payment_status}
                      </span>
                    </div>
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-400 uppercase font-semibold mb-2">Sipariş İçeriği ({selectedOrder.item_count})</p>
                    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                      <table className="w-full text-[12px]">
                        <thead><tr className="bg-gray-50/50 border-b border-gray-100">
                          <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500">Ürün</th>
                          <th className="text-center px-2 py-2 text-xs font-semibold text-gray-500 w-14">Adet</th>
                          <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500 w-24">Birim</th>
                          <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500 w-24">Toplam</th>
                        </tr></thead>
                        <tbody>
                          {(selectedOrder.items || []).map((item, idx) => (
                            <tr key={idx} className={`border-b border-gray-100 last:border-0 ${idx % 2 === 1 ? 'bg-gray-50/30' : 'bg-white'}`}>
                              <td className="px-3 py-2"><p className="font-medium text-gray-900 truncate">{item.product_name}</p>
                                <p className="text-[10px] text-gray-400">
                                  {item.iskonto_orani > 0 && <span className="text-[#1a224f] font-medium">%{item.iskonto_orani} isk.</span>}
                                  {item.kdv_orani > 0 && <span className="ml-1">+%{item.kdv_orani} KDV</span>}
                                </p>
                              </td>
                              <td className="px-2 py-2 text-center tabular-nums">{item.quantity}</td>
                              <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(item.unit_price)}</td>
                              <td className="px-3 py-2 text-right font-semibold tabular-nums">{formatCurrency(item.total_price)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-100 space-y-1">
                    {(() => {
                      const net = (selectedOrder.items || []).reduce((s, i) => s + (i.unit_price * i.quantity), 0);
                      const kdv = (selectedOrder.items || []).reduce((s, i) => s + (i.unit_price * i.quantity * (i.kdv_orani || 0) / 100), 0);
                      return (
                        <>
                          <div className="flex justify-between text-xs"><span className="text-gray-500">Net</span><span className="font-medium tabular-nums">{formatCurrency(net)}</span></div>
                          {kdv > 0 && <div className="flex justify-between text-xs"><span className="text-gray-500">KDV</span><span className="font-medium tabular-nums">{formatCurrency(kdv)}</span></div>}
                          <div className="flex justify-between text-sm font-bold pt-1.5 border-t border-gray-200"><span>Toplam</span><span className="text-[#1a224f] tabular-nums">{formatCurrency(net + kdv)}</span></div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
